import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ALL_NUVOLA_COLUMNS, FOOTER_COLUMNS, PAGE_TRAILS, ROUTES, SECTION_MENUS } from './siteNav';
import GetNoScamButtons from './GetNoScamButtons';
import { GET_STARTED_BLOCKS } from './getStartedContent';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { API_BASE_URL, fetchWithCsrf } from '../../config/api';

const PANEL_BG = '#D9D9D9';
// Every topic panel is the same height so the tabs don't jump as you move
// across the nav.
const PANEL_MIN_H = 520;
// Ordinary bottom padding. This used to reserve room for an oversized
// "No / SCAM" wordmark pinned to the base of every panel; that was removed.
const PANEL_BOTTOM_SPACE = 'pb-10 lg:pb-12';

/* ------------------------------------------------------------------ *
 * Small shared pieces
 * ------------------------------------------------------------------ */

// "Learn more.  →" style link: label, trailing arrow, rule underneath.
const RuleLink = ({ to, children, dark = false, className = '' }) => (
  <Link to={to} className={`inline-block group ${className}`}>
    <span
      className={`flex items-center gap-6 text-[15px] md:text-base ${
        dark ? 'text-white' : 'text-black'
      }`}
    >
      <span>{children}</span>
      <span aria-hidden="true">&rarr;</span>
    </span>
    <span
      className={`mt-1 block h-px w-full ${
        dark ? 'bg-white/70 group-hover:bg-white' : 'bg-black/50 group-hover:bg-black'
      }`}
    />
  </Link>
);

// The pair of actions every topic panel carries. Both sit on the same
// baseline so their labels and underlines line up. 10-1: no full stops.
const PanelActions = ({ learnMoreTo, dark = false }) => (
  <div className="flex flex-wrap items-start gap-x-10 gap-y-4 md:gap-x-16">
    <RuleLink to={learnMoreTo} dark={dark}>
      Learn more
    </RuleLink>
    <RuleLink to={ROUTES.getStarted} dark={dark}>
      Get No / SCAM
    </RuleLink>
  </div>
);

// The "No / SCAM ->" prefix was dropped per the 8-22 review.
const PanelHeading = ({ children, dark = false }) => (
  <h2 className={`text-[17px] font-bold md:text-lg ${dark ? 'text-white' : 'text-black'}`}>
    {children}
  </h2>
);

// Shared inner width for every topic panel.
const PanelInner = ({ children, className = '' }) => (
  <div
    className={`relative mx-auto w-full max-w-[1500px] px-5 pt-7 md:px-10 ${PANEL_BOTTOM_SPACE} ${className}`}
  >
    {children}
  </div>
);

/* ------------------------------------------------------------------ *
 * Panels — one per top-level nav item
 * ------------------------------------------------------------------ */

/*
 * The first three topic panels share one layout (10-1 round): a white copy
 * card on the left, headed by the AI cloud mark, and a rounded photo card on
 * the right with a caption set over its top-left corner. They used to be three
 * different treatments — a card beside a photo, a card over a full-bleed
 * backdrop, and bare copy on a portrait.
 */
const TopicPanel = ({ heading, children, learnMoreTo, image, imagePosition = 'object-center', caption, captionLight = false }) => (
  <PanelInner>
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,38%)_minmax(0,1fr)] lg:gap-10">
      {/* The copy card is the grid item itself and a column, so it stretches
          to the photo's height and the two actions sit at its foot - any
          spare room opens up between the paragraph and the actions, and the
          card and the photo always start and finish together. */}
      <div className="flex flex-col rounded-[28px] bg-white px-7 py-6 shadow-sm">
          <div className="flex items-end gap-3">
            <img
              src="/images/site/nav/ai-cloud-black.png"
              alt=""
              aria-hidden="true"
              className="h-10 w-auto flex-shrink-0 md:h-12"
            />
            <h2 className="text-[20px] font-bold leading-tight text-black md:text-[24px]">
              {heading}
            </h2>
          </div>
          <p className="mt-4 text-[15px] leading-relaxed text-black md:text-base">{children}</p>
          <div className="mt-auto pt-8">
            <PanelActions learnMoreTo={learnMoreTo} />
          </div>
      </div>

      {/* The photo fills the card's full height, which the copy card beside
          it sets, so no ground shows beneath a short crop. */}
      <div className="relative min-h-[220px] overflow-hidden rounded-[28px] shadow-[0_6px_14px_rgba(0,0,0,0.35)] md:min-h-[290px]">
        <img
          src={image}
          alt=""
          aria-hidden="true"
          className={`absolute inset-0 h-full w-full object-cover ${imagePosition}`}
        />
        {/* Caption lines break where the mockup breaks them (slides 7–9),
            never mid-phrase. */}
        <p
          className={`absolute left-6 top-6 text-[20px] font-bold leading-tight md:left-10 md:top-9 md:text-[26px] ${
            captionLight ? 'text-white [text-shadow:0_1px_6px_rgba(0,0,0,0.8)]' : 'text-black'
          }`}
        >
          {caption.map((line) => (
            <span key={line} className="block whitespace-nowrap">
              {line}
            </span>
          ))}
        </p>
      </div>
    </div>
  </PanelInner>
);

// Paradigm shift (formerly Evolution of Innovation).
const EvolutionPanel = () => (
  <TopicPanel
    heading="Built for you."
    learnMoreTo={ROUTES.happensToOtherPeople}
    image="/images/site/nav/paradigm-shift.jpeg"
    caption={['Art of the possible.']}
    captionLight
  >
    Easily make your browser ScammerAware. With No&nbsp;/&nbsp;SCAM all your
    scammer prevention tools must haves are in one application. This empowers
    you with the information you need to avoid being a scammer&rsquo;s next
    victim.
  </TopicPanel>
);

// ScammerAware (formerly HITL-AI).
const HitlPanel = () => (
  <TopicPanel
    heading="Tools that work for you."
    learnMoreTo={ROUTES.neurodivergentExplained}
    image="/images/site/nav/hitl-ai.jpeg"
    imagePosition="object-right"
    caption={['Transforming how you', 'surf the web.']}
  >
    This design approach makes you ScammerAware, an active informed
    participant. No&nbsp;/&nbsp;SCAM&rsquo;s HITL architecture seamlessly blends
    scammer threat intelligence detection with human informed oversight
    creating intentional checkpoints enabling you to make informed scammer risk
    decisions.
  </TopicPanel>
);

// Brainwashing. The couple at the laptop moved here from Paradigm shift.
const BrainwashingPanel = () => (
  <TopicPanel
    heading="The power of knowing."
    learnMoreTo={ROUTES.abusePlaybook}
    image="/images/site/nav/evolution-of-innovation.jpeg"
    // The man sits ~45% across; at lg the card is tall and narrow enough that
    // a centred crop lands his face under the caption. 35% keeps him right.
    imagePosition="object-[35%_50%]"
    caption={['Unmasking the', 'fraudsters.']}
  >
    Understand the manipulation techniques scammers use to influence and
    control others for their own gain. Explore the dark underbelly of scammer
    psychology &mdash; the manipulative techniques, tactics and strategies used
    to exploit individuals&rsquo; vulnerabilities.
  </TopicPanel>
);

const WRECKAGE_STATS = [
  { label: 'Victims we abused', value: '4.2 Million' },
  { label: '$$$ stolen', value: '$50 Billion' },
  { label: 'Average Number of victims', value: '836,000' },
];

// Human Wreckage: stats, the eye photograph and the actions down the left,
// the statement on the right. The two columns only meet in the desktop
// dropdown; the mobile drawer is always narrower than lg, so there it stacks
// with the statement first and the actions last.
const WreckagePanel = () => (
  <>
    <div className="absolute inset-0 bg-black" />
    <PanelInner>
      <PanelHeading dark>No / SCAM &rarr; Don&rsquo;t be a victim.</PanelHeading>

      <div className="mt-5 grid grid-cols-1 gap-8 lg:grid-cols-2 lg:gap-12">
        <div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            {WRECKAGE_STATS.map((stat) => (
              <div
                key={stat.label}
                className="flex flex-col items-center justify-center rounded-[10px] border border-[#6fa8dc] px-3 py-3 text-center text-[15px] leading-snug text-white"
              >
                <span>{stat.label} /</span>
                <span>{stat.value}</span>
              </div>
            ))}
          </div>

          <img
            src="/images/site/nav/human-wreckage.jpeg"
            alt=""
            aria-hidden="true"
            className="mt-6 h-auto w-full rounded-[22px] border border-white/60"
          />

          <div className="mt-6 pl-1">
            <PanelActions learnMoreTo={ROUTES.predatorProfile} dark />
          </div>
        </div>

        <p className="order-first text-[20px] font-bold leading-snug text-white md:text-[24px] lg:order-none xl:text-[26px]">
          The current technological solutions are a nuisance easily bypassed by fraudsters
          &ndash; if they worked there would be some evidence &ndash; SOMEWHERE.
        </p>
      </div>
    </PanelInner>
  </>
);

const GetStartedBlock = ({ number, heading, items }) => (
  <div>
    <h3 className="text-[16px] text-black md:text-[17px]">
      {String(number).padStart(2, '0')} / {heading}
    </h3>
    {items && (
      <ul className="mt-1 space-y-0.5">
        {items.map((item) => (
          <li key={item} className="flex gap-2.5 text-[15px] leading-snug text-black md:text-base">
            <span aria-hidden="true" className="mt-[2px] text-xs">
              &bull;
            </span>
            <span>{item}</span>
          </li>
        ))}
      </ul>
    )}
  </div>
);

// Blocks are numbered by their position in the list, so the numbers survive a
// block being added or dropped.
const getStartedColumn = (column) =>
  GET_STARTED_BLOCKS.map((block, index) => ({ ...block, number: index + 1 })).filter(
    (block) => block.column === column
  );

// Get Started: three-column product summary closing on the download buttons.
// Exported so the panel stays available to other entry points (the bar no
// longer opens it since 10-28).
export const GetStartedPanel = () => (
  <PanelInner>
    {/* This panel keeps the "No / SCAM »" prefix -- the mockup shows it here.
        The four topic panels had theirs removed; this one did not. */}
    <h2 className="text-[26px] font-bold leading-tight text-black md:text-[32px]">
      No / SCAM &raquo; Easy to use. Easy to love.
    </h2>

    <div className="mt-6 grid grid-cols-1 gap-8 md:grid-cols-[minmax(0,32%)_minmax(0,1fr)_minmax(0,26%)] md:gap-10">
      <div>
        <p className="text-[16px] font-light leading-snug text-black md:text-[18px]">
          Reclaim your surfing experience by turning your browser into a
          ScamAware browser. Experience what being truly ScamAware feels like.
          This next generation scammer prevention tool is unmatched &mdash;
          don&rsquo;t fall for being a scammer&rsquo;s next unsuspecting victim.
        </p>
        <span aria-hidden="true" className="mb-5 mt-4 block h-px w-[85%] bg-black/60" />
        {getStartedColumn(0).map((block) => (
          <GetStartedBlock key={block.heading} {...block} />
        ))}
      </div>

      <div className="space-y-6">
        {getStartedColumn(1).map((block) => (
          <GetStartedBlock key={block.heading} {...block} />
        ))}
      </div>

      <div className="space-y-6">
        {getStartedColumn(2).map((block) => (
          <GetStartedBlock key={block.heading} {...block} />
        ))}
        <div>
          <GetStartedBlock number={GET_STARTED_BLOCKS.length + 1} heading="Get No / SCAM" />
          <GetNoScamButtons className="mt-4 space-y-4" />
        </div>
      </div>
    </div>
  </PanelInner>
);

// All NuVola AI: the whole directory. It carries the same six columns as the
// footer sitemap — the content sections plus Trending / About / Legal — so
// every page in the site is reachable from the nav. Still anchored to the
// right rather than running the full width of the header.
const ALL_NUVOLA_DIRECTORY = [...FOOTER_COLUMNS, ...ALL_NUVOLA_COLUMNS];

const AllNuvolaPanel = () => (
  <div
    className="ml-auto w-full max-w-[1280px] px-8 py-9 shadow-xl md:px-12"
    style={{ backgroundColor: PANEL_BG }}
  >
    <div className="grid grid-cols-2 gap-x-8 gap-y-8 sm:grid-cols-3 lg:grid-cols-6">
      {ALL_NUVOLA_DIRECTORY.map((column) => (
        <div key={column.heading}>
          <h3 className="text-lg font-bold text-black md:text-xl">{column.heading}</h3>
          <ul className="mt-3 space-y-2">
            {column.links.map((link) => (
              <li key={link.label}>
                <Link
                  to={link.to}
                  className="text-[15px] text-black hover:underline md:text-base"
                >
                  {link.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  </div>
);

/* ------------------------------------------------------------------ *
 * Header
 * ------------------------------------------------------------------ */

// Section dropdown on sub-pages: just the pages in that section. No imagery --
// the rich topic panels belong to the homepage nav.
const SectionPanel = ({ links }) => (
  <PanelInner>
    <ul className="grid grid-cols-1 gap-x-10 gap-y-2 sm:grid-cols-2 lg:grid-cols-4">
      {links.map((link) => (
        <li key={link.label}>
          <Link
            to={link.to}
            className="block py-1 text-[15px] text-black hover:underline md:text-base"
          >
            {link.label}
          </Link>
        </li>
      ))}
    </ul>
  </PanelInner>
);

// Where Get Started points for anyone who is not a known subscriber. A visitor
// with no account used to be sent to /welcome, which forwarded them to the
// Microsoft sign-in before they had seen a plan (10-29).
const GET_STARTED_FALLBACK = '/get-access';
const GET_STARTED_SUBSCRIBED = '/welcome';

const SECTION_ITEMS = SECTION_MENUS.map((section) => ({
  id: section.id,
  label: section.label,
  Panel: () => <SectionPanel links={section.links} />,
}));

const MENUS = [
  { id: 'evolution', label: 'Paradigm shift', Panel: EvolutionPanel },
  { id: 'hitl', label: 'ScammerAware', Panel: HitlPanel },
  { id: 'brainwashing', label: 'Brainwashing', Panel: BrainwashingPanel },
  { id: 'wreckage', label: 'Human Wreckage', Panel: WreckagePanel, dark: true },
  // 10-28: a plain link, no dropdown. GetStartedPanel is kept (the mobile
  // accordion and the Learn more / Get No / SCAM pairs still reference its
  // content) but no longer opens from the bar. The destination is resolved in
  // SiteHeader (10-29): subscribers go to the Download page, everyone else to
  // Get Access. GET_STARTED_FALLBACK is what the link reads before auth answers.
  { id: 'get-started', label: 'Get Started', to: GET_STARTED_FALLBACK },
];

const ALL_NUVOLA = {
  id: 'all',
  label: 'All NuVola AI',
  Panel: AllNuvolaPanel,
  bare: true,
};

/* ------------------------------------------------------------------ *
 * Account control — Sign In when signed out, account menu when signed in
 * ------------------------------------------------------------------ */

/*
 * Three distinct destinations:
 *   Account  → the dashboard page (subscription-gated by ProtectedRoute)
 *   Billing  → the Stripe customer portal, opened directly rather than routed
 *              through a dashboard page that only holds the same button
 *   Download → /welcome, which serves the installer and gates on subscription
 *
 * None of them are hidden for unsubscribed users: each destination already
 * routes to checkout on its own, so the menu never dead-ends. /payment/portal
 * even returns a Checkout URL when there is no Stripe customer yet.
 */
const ACCOUNT_LINKS = [
  { label: 'Account', to: '/dashboard/account-management' },
  { label: 'Download', to: '/welcome' },
];

/** First letter of the display name, falling back to the email. */
const initialFor = (user) => {
  const source = user?.name || user?.email || '';
  return source.trim().charAt(0).toUpperCase() || '?';
};

const AccountMenu = ({ user, isOpen, onToggle, onNavigate }) => {
  const label = user?.name || user?.email || 'My account';
  const [billingBusy, setBillingBusy] = useState(false);

  /**
   * Billing is the Stripe customer portal, so open it directly. The endpoint
   * hands back a Checkout URL instead when the user has no Stripe customer,
   * which keeps an unsubscribed visitor moving forward rather than stalling.
   */
  const openBilling = async () => {
    setBillingBusy(true);
    try {
      const res = await fetchWithCsrf(`${API_BASE_URL}/payment/portal`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ returnUrl: window.location.origin + '/welcome' }),
      });
      const data = await res.json();
      if (!res.ok || !data.url) {
        throw new Error(data.message || 'Could not open billing');
      }
      window.location.href = data.url;
    } catch (error) {
      console.error('[SiteHeader] Billing portal error:', error);
      // Manage-subscription also lives on /welcome, with the fuller explanation
      // of why a portal may be unavailable.
      setBillingBusy(false);
      onNavigate?.();
      window.location.href = '/welcome';
    }
  };

  return (
    <div className="relative">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={isOpen}
        aria-haspopup="menu"
        className="flex items-center gap-2.5 rounded-full border border-black/50 py-1 pl-1 pr-4 text-[15px] text-black transition-colors hover:bg-black/[.06] md:text-[16px]"
      >
        {/* Filled avatar + green dot: signed-in state readable at a glance */}
        <span className="relative flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-[#2F5F9E] text-[13px] font-bold text-white">
          {initialFor(user)}
          <span className="absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full border-2 border-[#D9D9D9] bg-[#2F6B2F]" />
        </span>
        <span className="max-w-[140px] truncate">{label}</span>
        <span aria-hidden="true" className="text-[13px] leading-none">
          {isOpen ? '^' : 'v'}
        </span>
      </button>

      {isOpen && (
        <div
          role="menu"
          className="absolute right-0 top-full z-50 mt-1 min-w-[240px] py-2 shadow-xl"
          style={{ backgroundColor: PANEL_BG }}
        >
          <p className="px-5 pb-2 pt-1 text-[13px] text-black/60">Signed in as</p>
          <p className="truncate px-5 pb-2 text-[15px] font-semibold text-black">
            {user?.email || label}
          </p>
          <span className="my-1 block h-px bg-black/15" />

          <Link
            to={ACCOUNT_LINKS[0].to}
            role="menuitem"
            onClick={onNavigate}
            className="block px-5 py-2 text-[15px] text-black hover:bg-black/5"
          >
            {ACCOUNT_LINKS[0].label}
          </Link>

          <button
            type="button"
            role="menuitem"
            onClick={openBilling}
            disabled={billingBusy}
            className="block w-full px-5 py-2 text-left text-[15px] text-black hover:bg-black/5 disabled:opacity-60"
          >
            {billingBusy ? 'Opening billing…' : 'Billing'}
          </button>

          <Link
            to={ACCOUNT_LINKS[1].to}
            role="menuitem"
            onClick={onNavigate}
            className="block px-5 py-2 text-[15px] text-black hover:bg-black/5"
          >
            {ACCOUNT_LINKS[1].label}
          </Link>

          <span className="my-1 block h-px bg-black/15" />
          <a
            href={`${API_BASE_URL}/auth/logout?clearB2C=true`}
            role="menuitem"
            className="block px-5 py-2 text-[15px] text-black hover:bg-black/5"
          >
            Sign out
          </a>
        </div>
      )}
    </div>
  );
};

const NavButton = ({ item, isOpen, isCurrent = false, onToggle }) => item.to ? (
  <Link
    to={item.to}
    aria-current={isCurrent ? 'true' : undefined}
    className={`flex items-center gap-2 whitespace-nowrap px-1 py-2 text-[15px] transition-colors xl:text-[17px] ${
      isCurrent ? 'font-bold text-black' : 'text-black/85 hover:text-black'
    }`}
  >
    {item.label}
  </Link>
) : (
  <button
    type="button"
    onClick={() => onToggle(item.id)}
    aria-expanded={isOpen}
    aria-current={isCurrent ? 'true' : undefined}
    aria-controls={`nav-panel-${item.id}`}
    className={`flex items-center gap-2 whitespace-nowrap px-1 py-2 text-[15px] transition-colors xl:text-[17px] ${
      isOpen || isCurrent ? 'font-bold text-black' : 'text-black/85 hover:text-black'
    }`}
  >
    {item.label}
    <span aria-hidden="true" className="text-[13px] leading-none">
      {isOpen ? '^' : 'v'}
    </span>
  </button>
);

/**
 * `sections` swaps the topic menus for the three content sections. Nothing
 * passes it today: the nav bar is deliberately identical on the homepage and
 * every sub-page. Kept because a sub-page mockup showed the section variant,
 * and it is one prop away if that comes back.
 */
const SiteHeader = ({ sections = false }) => {
  const [openId, setOpenId] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const headerRef = useRef(null);
  const { loading, authenticated, user, subscription } = useSubscription();
  const { pathname } = useLocation();

  // Get Started: Download page for an active (or past-due) subscriber, Get
  // Access for everyone else — no account, signed in without a plan, or auth
  // still resolving. /get-access forwards subscribers to /welcome itself, so
  // the fallback is never a dead end if someone clicks before auth answers.
  const hasAccess =
    authenticated &&
    (subscription?.status === 'active' || subscription?.status === 'past_due');
  const getStartedTo = hasAccess ? GET_STARTED_SUBSCRIBED : GET_STARTED_FALLBACK;

  const primary = (sections ? SECTION_ITEMS : MENUS).map((item) =>
    item.id === 'get-started' ? { ...item, to: getStartedTo } : item
  );
  // The section this page belongs to, so the nav shows where you are.
  const currentSection = sections ? (PAGE_TRAILS[pathname] || [])[0] : null;

  const toggle = (id) => setOpenId((current) => (current === id ? null : id));

  // A link inside a panel closes it (10-07). Navigating to a new page already
  // did, because the page's layout remounts the header; a link to the page
  // you are already on (Get Started > "No / SCAM for Windows OS" while on
  // /get-access) changes no route, so the panel used to stay up over the page
  // it pointed at. Buttons (the macOS card) are left alone.
  const closeOnLink = (event) => {
    if (event.target.closest('a')) {
      setOpenId(null);
      setMobileOpen(false);
    }
  };

  // Close on Escape, and on any click that lands outside the header.
  useEffect(() => {
    if (!openId) return undefined;

    const onKeyDown = (event) => {
      if (event.key === 'Escape') setOpenId(null);
    };
    const onPointerDown = (event) => {
      if (headerRef.current && !headerRef.current.contains(event.target)) {
        setOpenId(null);
      }
    };

    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('mousedown', onPointerDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('mousedown', onPointerDown);
    };
  }, [openId]);

  const allItems = [...primary, ALL_NUVOLA];
  const active = allItems.find((item) => item.id === openId);

  // Every layout pins the header with sticky top-0. With the mobile accordion
  // open the header can run well past the viewport, and a sticky element
  // taller than the viewport never scrolls its lower half into view -- so
  // while the menu is open the header itself becomes the scroll container,
  // capped at the viewport, with the bar row pinned inside it so the close
  // button stays reachable. Desktop is untouched: the absolutely positioned
  // panels there must not be clipped.
  return (
    <header
      ref={headerRef}
      className={`relative z-50 w-full ${
        mobileOpen ? 'max-h-[100dvh] overflow-y-auto overscroll-contain lg:max-h-none lg:overflow-visible' : ''
      }`}
    >
      <div style={{ backgroundColor: PANEL_BG }}>
        <div
          className={`mx-auto flex w-full max-w-[1500px] items-center gap-4 px-4 py-3 md:px-8 ${
            mobileOpen ? 'sticky top-0 z-10 lg:static' : ''
          }`}
          style={mobileOpen ? { backgroundColor: PANEL_BG } : undefined}
        >
          <Link to="/" className="flex-shrink-0" aria-label="NuVola AI home">
            <img
              src="/images/site/nav/nuvola-logo-black.png"
              alt="NuVola AI"
              className="h-9 w-auto md:h-10"
            />
          </Link>

          {/* The desktop row appears at lg, but at 1024-1280 its contents are
              wider than the viewport -- the overflow showed as the Sign In pill
              hanging off the right edge. Type and gutters stay compact through
              that band and step up at xl. */}
          <nav className="hidden min-w-0 flex-1 items-center justify-between lg:flex">
            <div className="flex flex-1 items-center justify-evenly">
              {primary.map((item) => (
                <NavButton
                  key={item.id}
                  item={item}
                  isOpen={openId === item.id}
                  isCurrent={item.label === currentSection}
                  onToggle={toggle}
                />
              ))}
            </div>
            <NavButton
              item={ALL_NUVOLA}
              isOpen={openId === ALL_NUVOLA.id}
              onToggle={toggle}
            />

            {/* Account access sits apart from the topic menus, at the far right.
                Reserve the slot while auth resolves so it doesn't flash "Sign In"
                at someone who is already signed in. */}
            <div className="ml-3 flex-shrink-0 xl:ml-6">
              {loading ? (
                <span aria-hidden="true" className="block h-[34px] w-[96px]" />
              ) : authenticated ? (
                <AccountMenu
                  user={user}
                  isOpen={openId === 'account'}
                  onToggle={() => toggle('account')}
                  onNavigate={() => setOpenId(null)}
                />
              ) : (
                <Link
                  to={ROUTES.signIn}
                  className="block whitespace-nowrap rounded-full border border-black/50 px-4 py-1.5 text-[15px] text-black transition-colors hover:bg-black hover:text-white xl:px-5 xl:text-[17px]"
                >
                  Sign In
                </Link>
              )}
            </div>
          </nav>

          <button
            type="button"
            className="ml-auto p-2 text-black lg:hidden"
            onClick={() => setMobileOpen((open) => !open)}
            aria-label="Toggle menu"
            aria-expanded={mobileOpen}
          >
            <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d={mobileOpen ? 'M6 18L18 6M6 6l12 12' : 'M4 6h16M4 12h16M4 18h16'}
              />
            </svg>
          </button>
        </div>

        {/* Mobile: accordion of the same panels */}
        {mobileOpen && (
          <div onClick={closeOnLink} className="border-t border-black/15 lg:hidden">
            {allItems.map((item) => (
              <div key={item.id} className="border-b border-black/10">
                {item.to ? (
                  <Link
                    to={item.to}
                    onClick={() => setMobileOpen(false)}
                    className="flex w-full items-center justify-between px-5 py-4 text-left text-base text-black"
                  >
                    <span>{item.label}</span>
                    <span aria-hidden="true">&rarr;</span>
                  </Link>
                ) : (
                  <button
                    type="button"
                    onClick={() => toggle(item.id)}
                    aria-expanded={openId === item.id}
                    className="flex w-full items-center justify-between px-5 py-4 text-left text-base text-black"
                  >
                    <span className={openId === item.id ? 'font-bold' : ''}>{item.label}</span>
                    <span aria-hidden="true">{openId === item.id ? '^' : 'v'}</span>
                  </button>
                )}
                {!item.to && openId === item.id && (
                  <div id={`nav-panel-${item.id}`} className="relative">
                    <item.Panel />
                  </div>
                )}
              </div>
            ))}

            {authenticated ? (
              <div className="px-5 py-4">
                <p className="flex items-center gap-2.5 text-base font-semibold text-black">
                  <span className="relative flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-[#2F5F9E] text-[13px] font-bold text-white">
                    {initialFor(user)}
                    <span className="absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full border-2 border-[#D9D9D9] bg-[#2F6B2F]" />
                  </span>
                  <span className="truncate">{user?.name || user?.email || 'My account'}</span>
                </p>
                <div className="mt-3 space-y-2">
                  <Link
                    to={ACCOUNT_LINKS[0].to}
                    onClick={() => setMobileOpen(false)}
                    className="block text-[15px] text-black"
                  >
                    {ACCOUNT_LINKS[0].label}
                  </Link>
                  {/* Billing lives on /welcome for mobile — same portal button,
                      with the explanation when a portal is unavailable. */}
                  <Link
                    to="/welcome"
                    onClick={() => setMobileOpen(false)}
                    className="block text-[15px] text-black"
                  >
                    Billing
                  </Link>
                  <Link
                    to={ACCOUNT_LINKS[1].to}
                    onClick={() => setMobileOpen(false)}
                    className="block text-[15px] text-black"
                  >
                    {ACCOUNT_LINKS[1].label}
                  </Link>
                  <a
                    href={`${API_BASE_URL}/auth/logout?clearB2C=true`}
                    className="block text-[15px] text-black"
                  >
                    Sign out
                  </a>
                </div>
              </div>
            ) : (
              <Link
                to={ROUTES.signIn}
                onClick={() => setMobileOpen(false)}
                className="block px-5 py-4 text-base font-semibold text-black"
              >
                Sign In
              </Link>
            )}
          </div>
        )}
      </div>

      {/* Desktop: the page behind an open panel blurs so the panel reads as a
          layer over the site. Anchored to top-full so it starts below the nav
          bar -- the bar itself stays sharp. Clicking it dismisses the panel:
          this sits inside the header, so the outside-click handler above never
          sees it and has to be duplicated here. Painted before the panel so
          the panel's own background covers it. */}
      {active && (
        <div
          role="presentation"
          onClick={() => setOpenId(null)}
          className="absolute inset-x-0 top-full hidden h-screen backdrop-blur-md lg:block"
        />
      )}

      {/* Mobile: the same blur behind the open hamburger menu. The desktop
          layer can't serve here -- while the menu is open the header is its own
          scroll box, which clips anything placed below it -- so this one is
          fixed to the viewport. At -z-10 inside the header it paints behind
          the menu but still above the page, and a tap on it closes the menu. */}
      {mobileOpen && (
        <div
          role="presentation"
          onClick={() => setMobileOpen(false)}
          className="fixed inset-0 -z-10 backdrop-blur-md lg:hidden"
        />
      )}

      {/* Desktop: overlay panel */}
      {active &&
        (active.bare ? (
          // Right-anchored directory; the rest of the row stays clear.
          <div
            id={`nav-panel-${active.id}`}
            onClick={closeOnLink}
            className="absolute inset-x-0 top-full hidden lg:block"
          >
            <active.Panel />
          </div>
        ) : (
          <div
            id={`nav-panel-${active.id}`}
            onClick={closeOnLink}
            className="absolute inset-x-0 top-full hidden overflow-hidden shadow-xl lg:block"
            style={{ backgroundColor: PANEL_BG, minHeight: PANEL_MIN_H }}
          >
            <active.Panel />
          </div>
        ))}
    </header>
  );
};

export default SiteHeader;
